// ─── useSubscriberChat — subscriber-friendly factory state ─────
// Talks to the /api/subscriber-chat/* endpoints to fetch and update the
// per-subscriber chat state (mode, wizard/build/onboarding, language prefs),
// plus helpers for translation, mode decisions, suggestions, build progress,
// the deployed-app list, the App Idea Wizard, and the Spec Generator.

import { useCallback, useEffect, useState } from 'react';
import { authHeaders, refreshAndGetHeaders, sessionExpiredBus } from '../api';
export { investigationPreflight, investigationInstructions } from '../lib/chat-codex.js';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

export type ChatMode = 'app_wizard' | 'build_progress' | 'app_management' | 'sync_find_fix' | 'technical_support';
export type SubscriberLevel = 'non_technical' | 'business_technical' | 'technical';
// ocn-S278-P0: subscriber-facing autonomy axis (distinct from SubscriberLevel vocab axis).
export type AutonomyLevel = 'just_build' | 'review_and_approve';

export interface SubscriberChatSession {
  /** Server capability; absent on older deployments means no extra history polling. */
  railwayProgressEnabled?: boolean;
  sessionId: string;
  chatSessionId: string | null;
  entryKind?:'project'|'build';
  chatMode: ChatMode;
  subscriberLevel: SubscriberLevel;
  // ocn-S278-P0: null = opening sequence not yet completed. Explicit value = resolved.
  autonomyLevel: AutonomyLevel | null;
  wizardState: Record<string, unknown>;
  buildState: Record<string, unknown>;
  onboardingState: Record<string, unknown>;
  languagePreferences: Record<string, unknown>;
  lastActive: string;
  projectId: string | null;
  projectName?: string | null;
  title: string | null;
}

export interface TranslationResponse {
  original: string;
  translated: string;
  translationApplied: boolean;
  friendlyComponentName?: string;
  statusUpdate?: string;
}

export interface ModeDecisionResponse {
  mode: ChatMode;
  reason: string;
  shouldStartWizard: boolean;
  followUp?: 'wizard_questions' | 'build_progress' | 'app_list' | 'sync_run' | 'none';
  suggestedReply?: string;
}

export interface Suggestion {
  id: string;
  text: string;
  action:
    | 'show_build_progress'
    | 'open_app'
    | 'start_new_app'
    | 'show_examples'
    | 'modify_app'
    | 'view_billing'
    | 'answer_wizard_question'
    | 'resume_wizard'
    | 'contact_support';
  metadata?: Record<string, unknown>;
}

// ─── Grounded Help Answer (ocn-S88-P0 surface) ──────────────
// A single source the answer was grounded on. `uri` is the public
// wiki/KB link the help chat surfaces under the answer.
export interface AnswerSource {
  title: string | null;
  uri: string | null;
  similarity: number;
}

export interface SubscriberAnswerResponse {
  answer: string;
  grounded?: boolean;
  sources?: AnswerSource[];
  // The answer endpoint can also short-circuit into sync/find-fix flows.
  mode?: string;
  // ocn-S310-P0: set true when the answer is a findings detail drill-down (opt-in).
  // The frontend renders these with an inline expand/collapse element per contract #4.
  findings_detail?: boolean;
}

export interface FriendlyBuildProgress {
  runId: string;
  appName: string;
  overallProgress: number;
  estimatedMinutesRemaining: number | null;
  estimatedTimeRemaining: number | null;
  estimatedTimeRemainingFriendly: string | null;
  currentTask: {
    name: string;
    phase: 'starting' | 'analyzing' | 'implementing' | 'testing' | 'deploying';
    progress: number;
    statusMessage: string;
  };
  completedTaskCount: number;
  canCancel: boolean;
  canPause: boolean;
}


export interface SubscriberChatSyncResponse {
  run_id: string;
  status: string;
  stream_url?: string;
  token?: string;
  phases?: unknown[];
  estimate?: Record<string, unknown>;
  idempotent?: boolean;
}

export interface DeployedApp {
  appId: string;
  name: string;
  description: string;
  status: 'live' | 'building' | 'error' | 'paused';
  url: string | null;
  lastUpdated: string | null;
  canModify: boolean;
  canDuplicate: boolean;
}

// ─── App Idea Wizard ────────────────────────────────────────

export type WizardQuestionType = 'multiple_choice' | 'text' | 'number' | 'boolean';

export interface WizardQuestion {
  id: string;
  question: string;
  type: WizardQuestionType;
  options?: string[];
  required: boolean;
  reasoning: string;
}

export interface EnrichedRequirements {
  coreFeatures: Array<{ name: string; description: string; priority?: 'must' | 'should' | 'could' }>;
  userTypes: Array<{ name: string; description: string }>;
  dataModel: Array<{ entity: string; fields: string[]; description?: string }>;
  expectedUsers: number;
  scalingRequirements: { tier: 'small' | 'medium' | 'large'; notes?: string };
  integrationNeeds: Array<{ name: string; purpose: string }>;
  industry: string;
  complianceRequirements: string[];
  businessModel: 'internal_tool' | 'saas' | 'marketplace' | 'content' | 'unknown';
  recommendedStack: { frontend: string; backend: string; database: string; hosting: string };
  deploymentStrategy: { approach: string; notes?: string };
  securityLevel: 'basic' | 'enhanced' | 'enterprise';
  summary: string;
  productName?: string;
  scope_exclusions?: string[];
}

export type WizardStatus = 'awaiting_answers' | 'enriching' | 'awaiting_confirmation' | 'complete';

export interface ConfirmationSummary {
  productName?: string;
  oneLineDescription?: string;
  businessModel?: string;
  primaryUserType?: string;
  coreFeatures?: Array<{ name: string; description: string; priority?: string }>;
  archetypeHint?: string;
  scope_exclusions?: string[];
  // Pricing/build estimates are optional because ocn-cc may omit them.
  estimatedBuildTime?: number | null;
  estimated_build_time?: number | null;
  estimatedCredits?: number | null;
  estimated_credits?: number | null;
}

export interface WizardStartResponse {
  sessionId: string;
  status: 'awaiting_answers';
  questions: WizardQuestion[];
}

export interface WizardAnswerResponse {
  sessionId: string;
  status: WizardStatus;
  questions: WizardQuestion[];
  answers: Record<string, unknown>;
  missingRequired?: string[];
  enrichedRequirements?: EnrichedRequirements | null;
  confirmationRequired?: boolean;
  summary?: ConfirmationSummary;
}

export type WizardConfirmRequest =
  | { sessionId: string; action: 'accept' }
  | { sessionId: string; action: 'edit'; edits: Partial<EnrichedRequirements> };

export interface WizardSessionResponse {
  sessionId: string;
  status: WizardStatus;
  summary?: ConfirmationSummary;
}

// ─── Spec Generator ─────────────────────────────────────────

export type SpecGenerationStatus = 'pending' | 'in_progress' | 'complete' | 'failed';

export interface SpecGenerationSession {
  sessionId: string;
  wizardSessionId?: string | null;
  status: SpecGenerationStatus;
  specs?: Record<string, unknown> | null;
  taskGraph?: Record<string, unknown> | null;
  validation?: Record<string, unknown> | null;
  completenessScore?: string | null;
  estimatedBuildTime?: number | null;
  enrichedRequirements?: EnrichedRequirements | null;
  error?: string | null;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  if (sessionExpiredBus.expired) {
    throw new Error('Session expired — please sign in again');
  }

  const request = (auth: Record<string, string>) => fetch(`${API_BASE}/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  const auth = await authHeaders();
  // Never turn an auth-hydration/cache failure into an anonymous API call.
  if (!auth.Authorization) {
    sessionExpiredBus.emit();
    throw new Error('Session expired — please sign in again');
  }

  const response = await request(auth);
  if (response.status !== 401) return response;

  // Match the main API client's recovery path: refresh once and retry once.
  const refreshed = await refreshAndGetHeaders();
  if (!refreshed.Authorization) {
    sessionExpiredBus.emit();
    return response;
  }

  const retryResponse = await request(refreshed);
  if (retryResponse.status === 401) {
    sessionExpiredBus.emit();
  }
  return retryResponse;
}

// ocn-S809-P0: a typed `code` (e.g. project_not_found / project_not_yet_accessible)
// travels alongside the human-readable message so a caller can render a
// diagnosable recovery state instead of just showing raw error text.
export class ApiError extends Error {
  code?: string;
  status: number;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const message=body.error==='factory_initialization_uncertain'
      ? 'A build attempt is recorded, but initialization could not be confirmed. Reopen this same chat to inspect that attempt before starting another build.'
      : body.error || `API error: ${res.status}`;
    throw new ApiError(message, res.status, body.code);
  }
  return res.json() as Promise<T>;
}

export const subscriberChatApi = {
  createEntry: async (input: { kind: 'factory' | 'forge'; message?: string; repoUrl?: string }): Promise<{ projectId: string; sessionId: string; chatSessionId: string; name: string; title: string }> =>
    asJson(await authedFetch('/subscriber-chat/entries', { method: 'POST', body: JSON.stringify(input) })),
  getSession: async (chatSessionId?: string | null, projectId?: string | null): Promise<SubscriberChatSession> => {
    const params = new URLSearchParams();
    if (chatSessionId) params.set('chat_session_id', chatSessionId);
    if (projectId) params.set('projectId', projectId);
    const q = params.size ? `?${params.toString()}` : '';
    return asJson(await authedFetch(`/subscriber-chat/session${q}`));
  },
  getMessages: async (chatId: string): Promise<Array<{ id: string; sender?: string; body?: string; created_at?: string; metadata?: Record<string, unknown> }>> => {
    const result = await asJson<{ messages: Array<{ id: string; sender?: string; body?: string; created_at?: string; metadata?: Record<string, unknown> }> }>(await authedFetch(`/chats/${chatId}/messages?limit=200`));
    return result.messages;
  },
  createMessage: async (chatId: string, body: string, metadata?: Record<string, unknown>): Promise<void> => {
    await asJson(await authedFetch(`/chats/${chatId}/messages`, { method: 'POST', body: JSON.stringify({ body, metadata }) }));
  },

  createChat: async (projectId: string, title?: string): Promise<{ sessionId: string; chatSessionId: string; title: string }> =>
    asJson(await authedFetch('/subscriber-chat/chats', { method: 'POST', body: JSON.stringify({ projectId, title }) })),

  renameChat: async (sessionId: string, title: string): Promise<void> => {
    await asJson(await authedFetch(`/subscriber-chat/chats/${sessionId}`, { method: 'PATCH', body: JSON.stringify({ title }) }));
  },

  updateSession: async (
    sessionId: string,
    updates: Partial<{
      chatMode: ChatMode;
      subscriberLevel: SubscriberLevel;
      // ocn-S278-P0: autonomy axis (undefined = don't touch; null not sent)
      autonomyLevel: AutonomyLevel;
      wizardState: Record<string, unknown>;
      buildState: Record<string, unknown>;
      onboardingState: Record<string, unknown>;
      languagePreferences: Record<string, unknown>;
      chatSessionId: string | null;
    }>,
  ): Promise<Partial<SubscriberChatSession>> => {
    return asJson(await authedFetch(`/subscriber-chat/session/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }));
  },

  translate: async (
    text: string,
    opts: {
      subscriberLevel?: SubscriberLevel;
      componentType?: string;
      taskPhase?: 'starting' | 'analyzing' | 'implementing' | 'testing' | 'deploying';
      chatMessageId?: string;
    } = {},
  ): Promise<TranslationResponse> => {
    return asJson(await authedFetch('/subscriber-chat/translate', {
      method: 'POST',
      body: JSON.stringify({ text, ...opts }),
    }));
  },

  decideMode: async (message: string, sessionId?: string): Promise<ModeDecisionResponse> => {
    return asJson(await authedFetch('/subscriber-chat/decide-mode', {
      method: 'POST',
      body: JSON.stringify({ message, sessionId }),
    }));
  },

  // Grounded Q&A: answers common questions from the public knowledge corpus
  // and returns the wiki/KB sources it used. Drives the help-chat surface.
  answer: async (
    message: string,
    opts: { sessionId?: string; projectId?: string; history?: Array<{ role: 'user' | 'assistant'; text: string }> } = {},
  ): Promise<SubscriberAnswerResponse> => {
    return asJson(await authedFetch('/subscriber-chat/answer', {
      method: 'POST',
      body: JSON.stringify({
        message,
        sessionId: opts.sessionId,
        projectId: opts.projectId,
        history: opts.history,
      }),
    }));
  },


  runSync: async (projectId: string): Promise<SubscriberChatSyncResponse> => {
    return asJson(await authedFetch('/subscriber-chat/sync', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    }));
  },

  getSuggestions: async (opts: {
    chatSessionId?: string;
    subscriberChatId?: string;
  } = {}): Promise<{ suggestions: Suggestion[] }> => {
    const params = new URLSearchParams();
    if (opts.chatSessionId) params.set('chat_session_id', opts.chatSessionId);
    if (opts.subscriberChatId) params.set('subscriber_chat_id', opts.subscriberChatId);
    const q = params.toString() ? `?${params.toString()}` : '';
    return asJson(await authedFetch(`/subscriber-chat/suggestions${q}`));
  },

  getBuildProgress: async (runId: string): Promise<FriendlyBuildProgress> => {
    return asJson(await authedFetch(`/subscriber-chat/build-progress/${runId}`));
  },

  listApps: async (): Promise<{ apps: DeployedApp[] }> => {
    return asJson(await authedFetch('/subscriber-chat/apps'));
  },

  advanceOnboarding: async (sessionId: string, skip = false): Promise<{
    step?: string;
    progress?: number;
    isLastStep?: boolean;
    skipped?: boolean;
    welcomeMessage?: string;
  }> => {
    return asJson(await authedFetch('/subscriber-chat/onboarding/advance', {
      method: 'POST',
      body: JSON.stringify({ sessionId, skip }),
    }));
  },

  // ─── App Idea Wizard ─────────────────────────────────────
  startWizard: async (initialIdea: string): Promise<WizardStartResponse> => {
    return asJson(await authedFetch('/wizard/app-idea/start', {
      method: 'POST',
      body: JSON.stringify({ initialIdea }),
    }));
  },

  startWizardFromSpec: async (specText: string, fileName?: string): Promise<WizardAnswerResponse> => {
    return asJson(await authedFetch('/wizard/app-idea/start-from-spec', {
      method: 'POST',
      body: JSON.stringify({ specText, fileName }),
    }));
  },

  answerWizard: async (
    sessionId: string,
    answers: Record<string, unknown>,
  ): Promise<WizardAnswerResponse> => {
    return asJson(await authedFetch('/wizard/app-idea/answer', {
      method: 'POST',
      body: JSON.stringify({ sessionId, answers }),
    }));
  },

  wizardConfirm: async (input: WizardConfirmRequest): Promise<WizardSessionResponse> => {
    return asJson(await authedFetch('/wizard/app-idea/confirm', {
      method: 'POST',
      body: JSON.stringify(input),
    }));
  },

  getWizardRequirements: async (sessionId: string): Promise<WizardAnswerResponse & {
    initialIdea: string;
    createdAt: string;
    completedAt?: string;
  }> => {
    return asJson(await authedFetch(`/wizard/app-idea/requirements/${sessionId}`));
  },

  // ─── Spec Generator ──────────────────────────────────────
  generateSpecs: async (opts: {
    wizardSessionId?: string;
    enrichedRequirements?: EnrichedRequirements;
  }): Promise<{
    sessionId: string;
    status: SpecGenerationStatus;
    specs: Record<string, unknown>;
    validation: Record<string, unknown>;
  }> => {
    return asJson(await authedFetch('/spec-generator/generate', {
      method: 'POST',
      body: JSON.stringify(opts),
    }));
  },

  getSpec: async (sessionId: string): Promise<SpecGenerationSession> => {
    return asJson(await authedFetch(`/spec-generator/specs/${sessionId}`));
  },

  interviewNext: async (state: Record<string, unknown>) => asJson(await authedFetch('/v1/interview/next', {
    method: 'POST', body: JSON.stringify({ state }),
  })),
  interviewAnswer: async (state: Record<string, unknown>, questionId: string, value: unknown) => asJson(await authedFetch('/v1/interview/answer', {
    method: 'POST', body: JSON.stringify({ state, questionId, value }),
  })),
  interviewAssume: async (state: Record<string, unknown>, field: string, value: unknown) => asJson(await authedFetch('/v1/interview/assume', {
    method: 'POST', body: JSON.stringify({ state, field, value }),
  })),
  interviewManifest: async (state: Record<string, unknown>, domainNotes = '', brandTemplate?: string) => asJson(await authedFetch('/v1/interview/manifest', {
    method: 'POST', body: JSON.stringify({ state, domainNotes, brandTemplate }),
  })),

  // ─── Factory Builds (S21-P6-v2) ─────────────────────────
  startFactoryBuild: async (opts: {
    wizardSessionId?: string;
    enrichedRequirements?: EnrichedRequirements;
    capabilityManifest?: Record<string, unknown>;
    projectId?: string;
    initiatingChatSessionId?: string;
  }): Promise<{ factoryBuildId: string; sessionId: string; status: string }> => {
    return asJson(await authedFetch('/spec-generator/start', {
      method: 'POST',
      body: JSON.stringify(opts),
    }));
  },

  /**
   * Fetch the current factory_build aggregate. Supports conditional GET —
   * pass `etag` from a prior response to receive `null` on 304 Not Modified.
   */
  getFactoryBuild: async (
    buildId: string,
    etag?: string | null,
  ): Promise<{ view: FactoryBuildView | null; etag: string | null; notModified: boolean }> => {
    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;
    const res = await authedFetch(`/factory-builds/${buildId}`, { headers });
    if (res.status === 304) {
      return { view: null, etag: etag ?? null, notModified: true };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((body as { error?: string }).error ?? `API error: ${res.status}`);
    }
    const view = await res.json() as FactoryBuildView;
    return { view, etag: res.headers.get('ETag'), notModified: false };
  },

  cancelFactoryBuild: async (
    buildId: string,
  ): Promise<{ status: string; pendingCancelled?: number; runningMarked?: number; alreadyTerminal?: boolean }> => {
    return asJson(await authedFetch(`/factory-builds/${buildId}/cancel`, { method: 'POST' }));
  },

  // ocn-S800-P0: server-authoritative resumability. Owner-scoped single most-
  // relevant resumable build (never live/cancelled — see route doc).
  getBuildState: async (chatSessionId?:string): Promise<{ activeBuild: BuildStateResponse | null }> => {
    return asJson(await authedFetch(`/subscriber/build-state${chatSessionId?`?chatSessionId=${encodeURIComponent(chatSessionId)}`:''}`));
  },
};

// ocn-S800-P0: mirrors GET /api/subscriber/build-state's response shape.
export interface BuildStateResponse {
  factoryBuildId: string;
  status: FactoryBuildView['status'];
  appName: string | null;
  specSessionId: string | null;
  wizardSessionId: string | null;
  projectId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface FactoryBuildPhase {
  componentName: string;
  status: string;
  progressPercent: number;
  phase: string | null;
  deployedUrl: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

// ocn-S730-P0: real per-chain-step progress (mirrors apps/api/src/routes/
// factory-builds.ts ChainStepView/ChainStepProgress exactly — this is what
// the chain driver actually emits per step, one entry per stepIndex holding
// only its LATEST known transition).
export interface ChainStepView {
  stepIndex: number;
  totalSteps: number;
  status: 'started' | 'completed' | 'failed';
  stage: string | null;
  label: string | null;
  percent: number;
  emittedAt: string;
}

export interface ChainStepProgress {
  currentStepIndex: number | null;
  totalSteps: number | null;
  percent: number | null;
  steps: ChainStepView[];
}

// ocn-S795-P0: mirrors apps/api/src/routes/factory-builds.ts's SubStepView /
// StageView exactly (separate apps, no shared import — keep in sync by hand).
export type SubStepStatus = 'upcoming' | 'in_progress' | 'completed' | 'failed' | 'skipped';
export type SubStepKind = 'chain_step' | 'provision' | 'fire' | 'phase_event';

export interface SubStepView {
  key: string;
  label: string;
  status: SubStepStatus;
  detail?: string | null;
  kind: SubStepKind;
  fireId?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  creditsUsed?: number | null;
  credits_used?: number | null;
}

export interface FactoryStageView {
  key: string;
  label: string;
  status: SubStepStatus;
  subSteps: SubStepView[];
}

export interface FactoryBuildView {
  id: string;
  status: 'spec_generating' | 'spec_generated' | 'building' | 'deploying' | 'live' | 'failed' | 'cancelled';
  subscriberUserId: string;
  wizardSessionId: string | null;
  specSessionId: string | null;
  projectId: string | null;
  specPath: string | null;
  progressPercent: number;
  phases: FactoryBuildPhase[];
  // null for legacy_worker builds (no factory_events per-step stream) or
  // before the first chain step event has landed.
  chainSteps: ChainStepProgress | null;
  // ocn-S795-P0: per-stage sub-step tree for the expandable drill-down.
  // Optional so pre-existing fixtures/tests that construct a FactoryBuildView
  // by hand (without this field) keep compiling; a real response always sets it.
  stages?: FactoryStageView[];
  deployment: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
}

// ─── Hook ────────────────────────────────────────────────────

export interface UseSubscriberChatResult {
  session: SubscriberChatSession | null;
  loading: boolean;
  error: string | null;
  // ocn-S809-P0: typed reason code (e.g. project_not_found /
  // project_not_yet_accessible) when the server supplied one, so a load
  // failure can render a diagnosable recovery state instead of raw text.
  errorCode: string | null;
  refresh: () => void;
  setMode: (mode: ChatMode) => Promise<void>;
  setLevel: (level: SubscriberLevel) => Promise<void>;
  // ocn-S278-P0: flip autonomy level mid-chat (opt-in escape hatch).
  // null resets to "sequence pending" (rare but possible).
  setAutonomy: (level: AutonomyLevel | null) => Promise<void>;
  runSync: (projectId: string) => Promise<SubscriberChatSyncResponse>;
}

export function useSubscriberChat(chatSessionId?: string | null, projectId?: string | null): UseSubscriberChatResult {
  const [session, setSession] = useState<SubscriberChatSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Never leave the previously scoped chat mounted while a new route/session
    // is resolving. Besides avoiding a misleading old transcript, this keeps
    // session-bound drafts and controls from appearing in a different chat.
    setSession(null);
    setLoading(true);
    setError(null);
    setErrorCode(null);

    subscriberChatApi.getSession(chatSessionId ?? null, projectId ?? null)
      .then(s => { if (!cancelled) { setSession(s); setLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setErrorCode(err instanceof ApiError ? err.code ?? null : null);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [chatSessionId, projectId, tick]);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  const setMode = useCallback(async (mode: ChatMode) => {
    if (!session) return;
    await subscriberChatApi.updateSession(session.sessionId, { chatMode: mode });
    setSession(s => (s ? { ...s, chatMode: mode } : s));
  }, [session]);

  const setLevel = useCallback(async (level: SubscriberLevel) => {
    if (!session) return;
    await subscriberChatApi.updateSession(session.sessionId, { subscriberLevel: level });
    setSession(s => (s ? { ...s, subscriberLevel: level } : s));
  }, [session]);

  // ocn-S278-P0: opt-in escape hatch — subscriber can flip autonomy mid-chat.
  const setAutonomy = useCallback(async (level: AutonomyLevel | null) => {
    if (!session) return;
    await subscriberChatApi.updateSession(session.sessionId, { autonomyLevel: level ?? undefined });
    setSession(s => (s ? { ...s, autonomyLevel: level } : s));
  }, [session]);

  const runSync = useCallback(async (projectId: string) => {
    return subscriberChatApi.runSync(projectId);
  }, []);

  return { session, loading, error, errorCode, refresh, setMode, setLevel, setAutonomy, runSync };
}
